import React, { useState, useMemo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Form,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from 'reactstrap';
import TableContainer from '../../../../Components/Common/TableContainerReactTable';
import classnames from 'classnames';
import FormInput from '../common/FormInput';
import SelectInput from '../common/SelectInput';
import LoadingIndicator from 'Components/Loading/LoadingIndicator';
import { useLazyGetWebsiteQuery, Website } from 'features/common/website';
import { useAssignProductWebsiteMutation } from 'features/products/redux/productApiSlice';


export interface WebsiteSearchForm {
  searchField: string;
  searchValue: string;
}

interface PickWebsiteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (websites: Website[]) => void;
  productRowId: number; // Add productRowId prop
  refetchProductWebsite: () => void;
}

const defaultSearchForm: WebsiteSearchForm = {
  searchField: 'Name',
  searchValue: ''
};


const searchFieldOptions = ['Name', 'Location', 'Type', 'Industry'];

const PickWebsiteModal: React.FC<PickWebsiteModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  productRowId,
  refetchProductWebsite // Add productRowId parameter
}) => {
  const [searchForm, setSearchForm] = useState<WebsiteSearchForm>(defaultSearchForm);
  const [selectedWebsites, setSelectedWebsites] = useState<Website[]>([]);
  const [activeTab] = useState('1'); // Only one tab, but keep for extensibility

  const handleSearchFieldChange = (field: keyof WebsiteSearchForm, value: string) => {
    setSearchForm(prev => ({ ...prev, [field]: value }));
  };

  //Redux query
  const [getWebsite, { data: websiteData, isLoading: isWebsiteLoading }] = useLazyGetWebsiteQuery();
  const [assignProductWebsite] = useAssignProductWebsiteMutation();

  const handleSearch = async () => {
    try {
      // Use utility function to format search parameters with '%' default
      const searchParam = searchForm.searchValue.trim() || '%';
      await getWebsite({ name: searchParam });
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const handleClear = () => {
    setSearchForm(defaultSearchForm);
    setSelectedWebsites([]);
  };

  const handleCheckboxChange = (website: Website, isChecked: boolean) => {
    if (isChecked) {
      setSelectedWebsites(prev => [...prev, website]);
    } else {
      setSelectedWebsites(prev => prev.filter(w => w.rowId !== website.rowId));
    }
  };

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      setSelectedWebsites(websiteData?.data || []);
    } else {
      setSelectedWebsites([]);
    }
  };

  const handleSelect = async () => {
    if (selectedWebsites.length === 0) return;

    try {
      // Call the API for each selected website
      const promises = selectedWebsites.map((website, index) =>
        assignProductWebsite({
          productRowId,
          body: {
            websiteId: website.rowId,
            status: 'Active', // Default status - you can modify this as needed
            order: index + 1 // Set order based on selection order
          }
        }).unwrap()
      );

      await Promise.all(promises);

      // Call the optional onSelect callback with selected websites
      if (onSelect) {
        onSelect(selectedWebsites);
      }

      // Reset selection and close modal
      setSelectedWebsites([]);
      onClose();
      refetchProductWebsite();
    } catch (error) {
      console.error('Failed to assign product websites:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleCancel = () => {
    setSelectedWebsites([]);
    onClose();
  };

  const handleClears = () => {
    setSelectedWebsites([]);
  };

  const isWebsiteSelected = (website: Website) => {
    return selectedWebsites.some(w => w.rowId === website.rowId);
  };

  const isAllSelected = () => {
    return websiteData?.data && websiteData.data.length > 0 &&
      selectedWebsites.length === websiteData.data.length;
  };

  // Define columns for the table
  const columns = useMemo(() => [
    {
      header: () => (
        <div className="d-flex justify-content-center align-items-center">
          <input
            type="checkbox"
            checked={isAllSelected()}
            onChange={(e) => handleSelectAll(e.target.checked)}
            className="form-check-input"
          />
        </div>
      ),
      accessorKey: 'pick',
      enableColumnFilter: false,
      size: 50,
      cell: (cell: any) => (
        <div className="d-flex justify-content-center align-items-center">
          <input
            type="checkbox"
            checked={isWebsiteSelected(cell.row.original)}
            onChange={(e) => handleCheckboxChange(cell.row.original, e.target.checked)}
            className="form-check-input"
          />
        </div>
      )
    },
    {
      header: 'Name',
      accessorKey: 'name',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div>
          <span className="text-link">
            {cell.getValue()}
          </span>
        </div>
      )
    },
    {
      header: 'Website URL',
      accessorKey: 'websiteUrl',
      enableColumnFilter: false
    },
    {
      header: 'Status',
      accessorKey: 'status',
      enableColumnFilter: false
    },
    {
      header: 'Active',
      accessorKey: 'activeFlag',
      enableColumnFilter: false
    },
  ], [selectedWebsites, websiteData]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>Pick Website</ModalHeader>
      <ModalBody>
        {/* Tab Navigation */}
        <Nav pills className="nav nav-tabs-custom arrow-navtabs nav-success mb-3">
          <NavItem>
            <NavLink
              style={{ cursor: 'pointer' }}
              className={classnames({ active: activeTab === '1' })}
              tabIndex={0}
              aria-label="Search Tab"
              onClick={() => { }}
              onKeyDown={() => { }}
            >
              Search
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={activeTab} className="text-muted">
          <TabPane tabId="1">
            <Form>
              {/* Removed the Search section label row for a cleaner look */}
              <Row className="mb-2 justify-content-center align-items-center gap-2">
                <Col md={4}>
                  <SelectInput
                    formData={searchForm}
                    setFormData={setSearchForm}
                    label="Search"
                    id="searchField"
                    options={searchFieldOptions}
                  />
                </Col>
                <Col md={4}>
                  <FormInput
                    id="searchValue"
                    label="For value"
                    value={searchForm.searchValue}
                    onChange={(val) => handleSearchFieldChange('searchValue', val)}
                    type="text"
                  />
                </Col>
                <Col md={1} className='me-3'>
                  <button
                    type="button"
                    onClick={() => handleSearch()}
                    style={{ minWidth: 80 }}
                    className="btn btn-soft-primary btn-sm"
                  >
                    Search
                  </button>
                </Col>
                <Col md={1} className=''>
                  <button
                    type="button"
                    onClick={handleClear}
                    style={{ minWidth: 80 }}
                    className="btn btn-soft-warning btn-sm"
                  >
                    Clear
                  </button>
                </Col>
              </Row>

              {/* Vendor Table */}
              <Row>
                <Col xs={12}>
                  {isWebsiteLoading ? (
                    <LoadingIndicator
                      isLoading={isWebsiteLoading}
                      isProcessing={false}
                      error={null}
                      title="Loading websites from server..."
                      height="200px"
                    />
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={websiteData?.data || []}
                      isGlobalFilter={false}
                      customPageSize={10}
                      divClass="mb-0"
                      tableClass="table-centered align-middle table-nowrap mb-0"
                      theadClass="table-light"
                      thClass="px-3 py-2"
                      verticalLines={true}
                    />
                  )}
                </Col>
              </Row>
            </Form>
          </TabPane>
        </TabContent>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between">
        <div className="text-muted">
          {selectedWebsites.length > 0 && (
            <span>{selectedWebsites.length} website{selectedWebsites.length !== 1 ? 's' : ''} selected</span>
          )}
        </div>
        <div className="d-flex gap-2">
          <button
            onClick={handleClears}
            className="btn btn-soft-warning btn-sm"
            style={{ minWidth: 80 }}
          >
            Clear
          </button>
          <button
            onClick={handleSelect}
            className="btn btn-soft-success btn-sm"
            style={{ minWidth: 80 }}
            disabled={selectedWebsites.length === 0}
          >
            Select
          </button>
          <button
            className="btn btn-soft-dark btn-sm"
            onClick={handleCancel}
            style={{ minWidth: 80 }}
          >
            Cancel
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default PickWebsiteModal;
